import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, RefreshCw, Trophy, Users, Receipt, IndianRupee, Gift, Ticket, Coins } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const ROW_PAGE = 50;
const num = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 });

/** What a customer (or one booking) got from the sale offers. */
const RewardChips = ({ r }) => {
  const chips = [];
  if (r.free_day_passes) chips.push(`+${r.free_day_passes} free Day Pass${r.free_day_passes === 1 ? '' : 'es'}`);
  if (r.bonus_meeting_credits) chips.push(`+${num(r.bonus_meeting_credits)} MR credits (${r.bonus_meeting_hours} hrs)`);
  if (r.bonus_purchase_credits) chips.push(`+${num(r.bonus_purchase_credits)} credits (2X)`);
  if (!chips.length) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col gap-1">
      {chips.map((c) => (
        <span key={c} className="inline-flex w-fit items-center rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap">{c}</span>
      ))}
      {(r.rewards_pending > 0 || r.reward_status === 'pending') && (
        <span className="text-[10px] text-amber-600">awaiting payment confirmation</span>
      )}
    </div>
  );
};

/**
 * Sale Tracking (Diwali Double-up) — leaderboard of customers by booking value
 * during the sale, built live from bookings, credit purchases and Virtual
 * Office payments (GET /api/sale-tracking). Cancelled / unpaid bookings never
 * count and refunds reduce value. Exports both views to Excel.
 */
export const SaleTrackingTab = ({ api, venues = [] }) => {
  const [filters, setFilters] = useState({ date_from: '', date_to: '', venue_id: 'all', product: 'all', offer: 'all', customer: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rowLimit, setRowLimit] = useState(ROW_PAGE);

  const params = useCallback((f) => {
    const p = {};
    if (f.date_from) p.date_from = f.date_from;
    if (f.date_to) p.date_to = f.date_to;
    if (f.venue_id !== 'all') p.venue_id = f.venue_id;
    if (f.product !== 'all') p.product = f.product;
    if (f.offer !== 'all') p.offer = f.offer;
    if (f.customer.trim()) p.customer = f.customer.trim();
    return p;
  }, []);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const res = await api.get('/sale-tracking', { params: params(f) });
      setData(res.data);
      setRowLimit(ROW_PAGE);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sale tracking');
    } finally {
      setLoading(false);
    }
  }, [api, params]);

  useEffect(() => { load(filters); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => {
    const f = { date_from: '', date_to: '', venue_id: 'all', product: 'all', offer: 'all', customer: '' };
    setFilters(f);
    load(f);
  };
  const showCustomer = (row) => {
    const f = { ...filters, customer: row.email || row.phone || row.customer_name };
    setFilters(f);
    load(f);
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get('/sale-tracking/export.xlsx', { params: params(filters), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(data?.sale?.name || 'sale').toLowerCase().replace(/\s+/g, '-')}-leaderboard-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};
  const board = data?.leaderboard || [];
  const rows = data?.rows || [];

  return (
    <div className="space-y-6" data-testid="sale-tracking-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-primary" /> {data?.sale?.name || 'Sale'} — Sale Tracking
            {data?.sale?.state && <Badge variant="outline" className="capitalize">{data.sale.state}</Badge>}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Customers ranked by booking value (excl. GST) paid {data?.window ? `${data.window.from} → ${data.window.to} IST` : 'during the sale'}.
            Cancelled, unpaid and refunded payments don't count.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => load(filters)} disabled={loading} data-testid="sale-tracking-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={exportExcel} disabled={exporting || !data} data-testid="sale-tracking-export">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Export to Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid gap-3 md:grid-cols-4 lg:grid-cols-7 items-end">
          <div>
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={filters.date_from} onChange={(e) => set('date_from', e.target.value)} data-testid="st-date-from" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={filters.date_to} onChange={(e) => set('date_to', e.target.value)} data-testid="st-date-to" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Centre</label>
            <Select value={filters.venue_id} onValueChange={(v) => set('venue_id', v)}>
              <SelectTrigger data-testid="st-centre"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All centres</SelectItem>
                {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Product</label>
            <Select value={filters.product} onValueChange={(v) => set('product', v)}>
              <SelectTrigger data-testid="st-product"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {(data?.products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Offer</label>
            <Select value={filters.offer} onValueChange={(v) => set('offer', v)}>
              <SelectTrigger data-testid="st-offer"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="any">Got any offer</SelectItem>
                {(data?.offers || []).map((o) => <SelectItem key={o.id} value={o.id}>Got {o.label}</SelectItem>)}
                <SelectItem value="none">No offer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Customer</label>
            <Input placeholder="Name, email or phone" value={filters.customer}
              onChange={(e) => set('customer', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(filters); }} data-testid="st-customer" />
          </div>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={() => load(filters)} disabled={loading} data-testid="st-apply">Apply</Button>
            <Button variant="ghost" onClick={reset} disabled={loading}>Reset</Button>
          </div>
          <p className="md:col-span-4 lg:col-span-7 text-[11px] text-muted-foreground">Leave dates empty to use the sale period.</p>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: 'Customers', value: summary.customers ?? '—', icon: Users },
          { label: 'Bookings & purchases', value: summary.sales ?? '—', icon: Receipt },
          { label: 'Booking value (excl. GST)', value: data ? inr(summary.booking_value) : '—', icon: IndianRupee },
          { label: 'Total paid (incl. GST)', value: data ? inr(summary.total_paid) : '—', icon: IndianRupee },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-2xl font-heading font-bold mt-1">{value}</p>
              </div>
              <Icon className="w-5 h-5 text-primary" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Offers given */}
      <div className="grid gap-4 md:grid-cols-4" data-testid="st-offer-summary">
        {[
          {
            label: 'Customers who got an offer', icon: Gift,
            value: data ? `${summary.customers_with_offers} of ${summary.customers}` : '—',
            sub: data && summary.rewards_pending ? `${summary.rewards_pending} reward(s) awaiting payment confirmation` : null,
          },
          { label: 'Free Day Passes given', icon: Ticket, value: data ? num(summary.free_day_passes) : '—' },
          {
            label: 'Bonus Meeting Room Credits', icon: Coins,
            value: data ? num(summary.bonus_meeting_credits) : '—',
            sub: data ? `${num(summary.bonus_meeting_hours)} free hours` : null,
          },
          { label: '2X bonus credits', icon: Coins, value: data ? num(summary.bonus_purchase_credits) : '—' },
        ].map(({ label, value, sub, icon: Icon }) => (
          <Card key={label} className="border-emerald-500/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-2xl font-heading font-bold mt-1">{value}</p>
                {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
              </div>
              <Icon className="w-5 h-5 text-emerald-600" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Leaderboard */}
      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-primary" /> Leaderboard</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {loading && !data ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : board.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No paid bookings in this period yet.</p>
          ) : (
            <Table data-testid="st-leaderboard">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Bookings</TableHead>
                  <TableHead className="text-right">Booking value</TableHead>
                  <TableHead className="text-right">Total paid</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Offers received</TableHead>
                  <TableHead>Centres</TableHead>
                  <TableHead>Booking dates</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {board.map((a) => (
                  <TableRow key={a.customer_key} className="cursor-pointer" onClick={() => showCustomer(a)} title="Show this customer's bookings">
                    <TableCell className="font-bold">{a.rank <= 3 ? ['🥇', '🥈', '🥉'][a.rank - 1] : a.rank}</TableCell>
                    <TableCell>
                      <p className="font-medium">{a.customer_name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{a.email}{a.phone ? ` · ${a.phone}` : ''}</p>
                      {a.company && <p className="text-xs text-muted-foreground">{a.company}</p>}
                    </TableCell>
                    <TableCell className="text-right">{a.total_bookings}</TableCell>
                    <TableCell className="text-right font-semibold">{inr(a.booking_value)}</TableCell>
                    <TableCell className="text-right">{inr(a.total_paid)}</TableCell>
                    <TableCell className="text-xs">{a.products.join(', ')}</TableCell>
                    <TableCell className="text-xs" data-testid="st-lb-rewards"><RewardChips r={a} /></TableCell>
                    <TableCell className="text-xs">{a.centres.join(', ') || '—'}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {a.booking_dates.length > 2 ? `${a.booking_dates[0]} → ${a.booking_dates[a.booking_dates.length - 1]} (${a.booking_dates.length})` : a.booking_dates.join(', ')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* All bookings */}
      <Card>
        <CardHeader><CardTitle className="text-lg">All bookings & purchases ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {rows.length === 0 ? (
            <p className="py-6 text-center text-muted-foreground">Nothing to show.</p>
          ) : (
            <>
              <Table data-testid="st-rows">
                <TableHeader>
                  <TableRow>
                    <TableHead>Paid at (IST)</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Centre</TableHead>
                    <TableHead>Booking date</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead>Sale reward</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, rowLimit).map((r) => (
                    <TableRow key={`${r.source}-${r.ref}`}>
                      <TableCell className="text-xs whitespace-nowrap">{r.paid_at}</TableCell>
                      <TableCell>
                        <p className="text-sm">{r.customer_name || '—'}</p>
                        <p className="text-xs text-muted-foreground">{r.email}</p>
                      </TableCell>
                      <TableCell className="text-xs">
                        <p className="font-medium">{r.product_label}</p>
                        <p className="text-muted-foreground">{r.item}{r.quantity ? ` · ${r.quantity}` : ''}</p>
                      </TableCell>
                      <TableCell className="text-xs">{r.centre || '—'}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{r.booking_date}</TableCell>
                      <TableCell className="text-xs">
                        {r.payment_method}{r.credits_used ? ` (${r.credits_used} cr)` : ''}
                        {r.sold_via && r.sold_via !== 'Online' && <p className="text-[11px] text-muted-foreground">{r.sold_via}</p>}
                      </TableCell>
                      <TableCell className="text-right text-sm">{inr(r.booking_value)}</TableCell>
                      <TableCell className="text-right text-sm">
                        {inr(r.total_paid)}
                        {r.refunded > 0 && <p className="text-[11px] text-destructive">−{inr(r.refunded)} refunded</p>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {r.offer_label && <p className="text-[11px] text-muted-foreground mb-1">{r.offer_label}</p>}
                        <RewardChips r={r} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length > rowLimit && (
                <div className="flex justify-center mt-3">
                  <Button variant="outline" size="sm" onClick={() => setRowLimit((n) => n + ROW_PAGE)}>
                    Show more ({rows.length - rowLimit} left)
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SaleTrackingTab;
